import { ComponentProps, FC, useMemo, useState } from 'react'
import { Button, DialogTitle, Modal, ModalCenter, Textarea } from 'src/components'
import { GroupRadio } from 'src/components/group-radio'
import { APPLICANT_REJECTION_REASONS } from 'src/constants'

interface IProps {
  isBulkAction?: boolean
  action?: (rejectionReason?: string) => void
}

export const ModalApplicantConfirmPassed: FC<ComponentProps<typeof Modal> & IProps> = ({ isBulkAction, action, ...props }) => {
  const [isReason, setIsReason] = useState(false)
  const [reason, setReason] = useState('Skills and Experience Do Not Closely Align with the Requirements of the Role.')
  const [otherReason, setOtherReason] = useState('')
  const rejectionReason = useMemo(() => reason === 'Other' ? otherReason : reason, [reason, otherReason])

  const title = useMemo(() => {
    if (isReason) {
      return 'Why Did You Reject This Applicant?'
    }

    if (isBulkAction) {
      return 'Pass these Applicants?'
    }

    return 'Pass this Applicant?'
  }, [isBulkAction, isReason])

  const subtitle = useMemo(() => {
    if (isBulkAction) {
      return 'The selected Applicants will be notified about the rejection.'
    }

    return 'The Applicant will be notified about the rejection.'
  }, [isBulkAction])

  const content = useMemo(() => {
    if (isBulkAction) {
      return "Before proceeding, please make sure you've watched their Vibe Checks and reviewed their profiles carefully. This helps ensure fair consideration and avoids missing out on potential great fits."
    }

    return "Before proceeding, please make sure you've watched their Vibe Check and reviewed their profile carefully. This helps ensure fair consideration and avoids missing out on potential great fits."
  }, [isBulkAction])

  return (
    <ModalCenter
      {...props}
      contentStyles={{
        width: isReason ? 672 : 424
      }}
      contentClass="p-10"
    >
      <div className="fx-column fx-ai-center gap-8">
        <div className="fx-column gap-2 fx-ai-center">
          <DialogTitle className="mb-3">
            {title}
          </DialogTitle>

          {isReason
            ? (
              <div className="fx-column gap-3">
                <GroupRadio
                  className="fx-column gap-3"
                  labelClassName="txt-black-02 fw-400"
                  inputStyle={{ width: 16, height: 16 }}
                  defaultValue={reason}
                  options={APPLICANT_REJECTION_REASONS}
                  onChange={reason => setReason(reason as string)}
                />

                {reason === 'Other' && (
                  <Textarea
                    placeholder="Tell Us Why"
                    onChange={e => setOtherReason(e.target.value)}
                    value={otherReason}
                  />
                )}
              </div>
            )
            : (
              <>
                <div className="fw-700 txt-negative-600">
                  This action CANNOT be undone.
                </div>

                <div className="txt-center lh-20 txt-black-02">
                  <span className="fw-700">{subtitle}</span><br/>
                  {content}
                </div>

                <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M28.09 51.96C33.98 61.07 45.75 65.7 50 75.68C44.02 73.43 38.04 71.17 32.06 68.92C31.3 68.64 30.46 68.35 29.71 68.66C28.12 69.31 28.54 71.7 29.45 73.16C33.56 79.82 41.32 83.13 47.35 88.12C54.38 93.94 59.44 102.41 67.78 106.11C77.5 110.43 89.22 106.87 97.53 100.23C102.87 95.96 107.35 90.23 108.75 83.53C109.7 78.99 109.2 74.28 108.69 69.67C107.99 63.34 107.3 57.01 106.6 50.68C106.27 47.68 104.84 43.74 101.85 44.14C100.59 44.31 99.6 45.28 98.81 46.27C93.74 52.6 92.87 61.21 92.21 69.29C85.71 59.23 79.21 49.17 72.71 39.11C69.92 34.79 67.11 30.44 63.67 26.62C62.09 24.86 59.79 23.08 57.59 23.96C54.52 25.18 55.28 29.71 56.66 32.7C61.01 42.1 66.2 51.1 72.15 59.57C59.51 52.55 56.1 35.32 44.49 26.7C43.35 25.85 41.98 25.06 40.58 25.31C37.66 25.82 37.3 29.94 38.22 32.76C42.29 45.28 56.15 53 59.22 65.8C49.53 61.39 44.44 50.81 36.59 43.62C28.32 36.04 22.66 43.56 28.1 51.97L28.09 51.96Z" fill="#AFF6C5"/>
                  <path d="M93.07 62.09C94.29 56.3 95.18 50.01 98.49 44.96C99.97 42.71 106.47 36.36 108.97 40.33C110.21 42.29 108.96 46.4 108.58 48.44C107.41 54.78 108.17 60.72 109.05 67.06C110.49 77.38 108.81 86.5 101.57 94.33C95.27 101.14 86.13 107.47 76.57 107.46C67.03 107.46 60.4 101.65 54.41 94.94C48.84 88.69 42.78 83.19 35.89 78.41C31.38 75.28 22.89 71.05 22.46 64.87C21.76 54.89 32.81 63.58 35.79 66.11C39.38 69.14 42.28 72.17 46.93 73.32C47.57 73.48 48.5 73.06 48.65 72.35C49.56 67.84 44.8 63.61 41.96 60.78C37.55 56.4 32.33 52.77 28.15 48.18C26.95 46.86 25.76 45.44 24.89 43.87C23.26 40.89 24.98 35.97 28.88 37.32C29.86 37.66 30.66 38.38 31.48 38.99C36.01 42.36 39.45 47.32 43.12 51.56C45.27 54.04 51.92 63 56.18 61.84C63.31 59.9 47.07 43.34 45.57 41.54C41.98 37.22 37.66 32.44 35.85 27.01C35.12 24.82 34.82 21.74 37.71 21.01C40.15 20.4 42.65 22.75 44.22 24.28C49.26 29.2 52.87 35.63 56.7 41.48C59.39 45.6 62.45 51.61 67.08 53.89C67.66 54.18 68.26 54.01 68.72 53.55C71.36 50.91 62.44 40.27 61.02 38.09C58.59 34.36 55.97 30.49 54.56 26.23C53.36 22.6 53.77 17.07 59.08 18.49C66.17 20.38 71.42 31.61 74.69 37.38C79.73 46.28 85.84 54.28 91.18 62.98C91.77 63.95 93.79 62.75 93.34 61.78C93.19 61.47 93.07 61.15 92.88 60.87C92.2 59.88 90.49 61.06 90.72 62.07L90.83 62.54C91.14 63.84 93.37 62.75 93.09 61.57L92.98 61.1L90.82 62.3L91.07 62.75L93.23 61.55C89.4 55.3 84.51 49.72 80.85 43.38C78.73 39.71 76.8 35.94 74.62 32.3C71.4 26.94 65.43 15.7 57.81 16.13C50.19 16.56 51.13 24.87 53.33 29.76C55.24 34.02 57.9 37.92 60.47 41.8C61.76 43.75 63.09 45.69 64.3 47.7C64.88 48.65 65.47 49.6 65.95 50.61C66.25 51.24 66.58 53 67.13 52.46L68.77 52.12C63.91 49.73 60.7 43.06 57.88 38.73C54.29 33.22 50.82 27.29 46.09 22.67C43.44 20.08 39.88 17.62 36.17 19.61C32.46 21.6 32.56 25.3 33.85 28.68C36.01 34.33 40.37 39.22 44.2 43.79C47.67 47.93 52.46 52.16 54.85 57.07C57.1 61.71 51.68 56.9 50.34 55.64C45.95 51.52 42.41 46.61 38.34 42.19C34.99 38.55 27.22 30.29 22.52 36.72C18.1 42.75 26.08 49.92 30.27 53.59C33.32 56.26 36.43 58.86 39.35 61.69C41.6 63.87 47.18 68.59 46.43 72.27L48.15 71.3C42 69.78 38.04 64.27 32.96 60.77C29.66 58.49 25 56.16 21.86 59.92C17.1 65.62 22.73 71.76 27.21 75.31C35.98 82.25 44.79 87.91 52.35 96.38C58.71 103.51 65.78 109.55 75.88 109.58C85.98 109.61 95.29 103.42 102.18 96.47C109.07 89.52 112.72 80.51 111.86 70.58C111.37 64.92 109.91 59.32 110.14 53.61C110.28 50.09 111.42 46.72 111.62 43.22C112.06 35.85 105.87 36.06 100.87 39.65C93.87 44.69 92.5 54.18 90.85 62.01C90.53 63.54 92.81 63.3 93.07 62.1L93.07 62.09Z" fill="black"/>
                  <path d="M88.09 9.77999C95.06 14.18 99.33 21.3 99.94 29.52C100.04 30.84 102.35 29.89 102.26 28.68C101.62 20.17 97.08 12.63 89.86 8.06999C88.83 7.41999 86.96 9.06999 88.09 9.77999Z" fill="black"/>
                  <path d="M81.95 18.65C87.02 22.37 90.19 27.72 91.05 33.94C91.23 35.27 93.53 34.31 93.37 33.1C92.47 26.57 89.04 20.84 83.73 16.94C82.74 16.21 80.87 17.85 81.96 18.65L81.95 18.65Z" fill="black"/>
                  <path d="M15.57 83.24C17.5 91.53 24.05 97.76 32.36 99.45C33.58 99.7 34.67 97.67 33.14 97.36C25.56 95.82 19.63 89.9 17.88 82.4C17.57 81.08 15.29 82.05 15.56 83.24L15.57 83.24Z" fill="black"/>
                  <path d="M6.39 90.4C9.58 101.11 18.77 108.8 29.87 110.08C31.1 110.22 32.2 108.17 30.65 107.99C20.3 106.8 11.68 99.54 8.71 89.56C8.32 88.25 6.04 89.23 6.39 90.4Z" fill="black"/>
                  <path d="M83.43 65.7C80.36 73.78 77.42 83.21 83.57 90.64C84.39 91.63 86.49 90.28 85.69 89.32C79.88 82.29 82.75 73.56 85.65 65.93C86.2 64.47 83.87 64.55 83.43 65.71L83.43 65.7Z" fill="black"/>
                </svg>
              </>
            )}
        </div>

        <div className="fx-column gap-3 w-100-p">
          <Button
            onClick={() => {
              isReason && action?.(rejectionReason)
              isReason && props.onClose?.()
              setIsReason(true)
            }}
          >
            {isReason ? 'Done' : 'Yes, Pass'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => props.onClose?.()}
          >
            Cancel
          </Button>
        </div>
      </div>
    </ModalCenter>
  )
}
